import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Tooltip,
} from 'chart.js';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { Col, DatePicker, Row, Spin } from 'antd';
import { toast } from 'react-toastify';
import { thongKeNhanSuDoanDangTheoPhongBan } from '../../services/nhanvien';
import { ThongKeNhanSuDoanDangResponse } from '../../models/nhanvien/NhanVien';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ThongKeNhanSuDangDoanPhongBan = () => {
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<ChartJS | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<ThongKeNhanSuDoanDangResponse[]>([]);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

    // Lấy dữ liệu từ api
    const loadData = async (date: Dayjs | null) => {
        try {
            setIsLoading(true);
            const ngayThongKeStr = date ? date.format('YYYY-MM-DD') : null;
            const result = await thongKeNhanSuDoanDangTheoPhongBan({ ngayThongKe: ngayThongKeStr });
            setData(result || []);
        } catch (err: any) {
            toast.error('Không thể tải dữ liệu thống kê Đảng - Đoàn!');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData(selectedDate);
    }, [selectedDate]);

    const handleDatePickerChange = (date: Dayjs | null) => {
        setSelectedDate(date || dayjs());
    };

    // Vẽ biểu đồ thống kê
    useEffect(() => {
        if (!chartRef.current || data.length === 0) return;
        chartInstance.current?.destroy();

        chartInstance.current = new ChartJS(chartRef.current, {
            type: 'bar',
            data: {
                labels: data.map(item => item.tenDonVi),
                datasets: [
                    {
                        label: 'Đảng viên chính thức',
                        data: data.map(item => item.soLuongDangVienChinhThuc),
                        backgroundColor: 'rgba(212, 83, 126, 0.85)',
                        borderColor: '#D4537E',
                        borderWidth: 1,
                        stack: 'dang',
                        barPercentage: 0.6,
                    },
                    {
                        label: 'Đảng viên dự bị',
                        data: data.map(item => item.soLuongDangVienDuBi),
                        backgroundColor: 'rgba(186, 117, 23, 0.8)',
                        borderColor: '#BA7517',
                        borderWidth: 1,
                        stack: 'dang',
                        barPercentage: 0.6,
                    },
                    {
                        label: 'Đoàn viên trưởng thành',
                        data: data.map(item => item.soLuongDoanVienTruongThanh),
                        backgroundColor: 'rgba(24, 95, 165, 0.85)',
                        borderColor: '#185FA5',
                        borderWidth: 1,
                        stack: 'doan',
                        barPercentage: 0.6,
                    },
                    {
                        label: 'Đoàn viên',
                        data: data.map(item => item.soLuongDoanVienChuaTruongThanh),
                        backgroundColor: 'rgba(83, 58, 183, 0.75)',
                        borderColor: '#533AB7',
                        borderWidth: 1,
                        stack: 'doan',
                        barPercentage: 0.6,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            title: (items) => items[0]?.label || '',
                            afterBody: (items) => {
                                const idx = items[0]?.dataIndex ?? 0;
                                const pb = data[idx];
                                const tongDang = pb.soLuongDangVienChinhThuc + pb.soLuongDangVienDuBi;
                                const tongDV = pb.soLuongDoanVienTruongThanh + pb.soLuongDoanVienChuaTruongThanh;
                                return [
                                    `Tổng Đảng viên: ${tongDang}`,
                                    `Tổng Đoàn viên: ${tongDV}`,
                                    `Tổng Nhân sự: ${pb.soLuongNhanVien}`
                                ];
                            },
                        },
                        backgroundColor: 'rgba(7,28,80,0.92)',
                        titleColor: '#ffffff',
                        bodyColor: '#c8d8f5',
                        padding: 10,
                        cornerRadius: 6,
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            font: { size: 11 },
                            color: '#5F5E5A',
                            maxRotation: 30,
                            autoSkip: false,
                            callback: function (val, index) {
                                const label = data[index]?.tenDonVi ?? '';
                                return label.length > 18 ? label.slice(0, 17) + '…' : label;
                            },
                        },
                        border: { display: false },
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(0,0,0,0.05)' },
                        ticks: { font: { size: 11 }, color: '#5F5E5A', precision: 0 },
                        border: { display: false },
                    },
                },
            },
        });

        return () => { chartInstance.current?.destroy(); };
    }, [data]);

    const tongSoNhanVien = data.reduce((s, d) => s + d.soLuongNhanVien, 0);
    const tongDangVienCT = data.reduce((s, d) => s + d.soLuongDangVienChinhThuc, 0);
    const tongDangVienDuBi = data.reduce((s, d) => s + d.soLuongDangVienDuBi, 0);
    const tongDangVien = tongDangVienCT + tongDangVienDuBi;

    const tongDoanVienTT = data.reduce((s, d) => s + d.soLuongDoanVienTruongThanh, 0);
    const tongDoanVienChuaTT = data.reduce((s, d) => s + d.soLuongDoanVienChuaTruongThanh, 0);
    const tongDoanVien = tongDoanVienTT + tongDoanVienChuaTT;

    const cardStyle: React.CSSProperties = {
        background: '#f5f7fb',
        borderRadius: 8,
        padding: '10px 16px',
        textAlign: 'center',
        minWidth: 120,
        flex: 1,
    };

    return (
        <Col xs={24} xl={24} style={{ marginTop: 16 }}>
            <Row style={{ marginBottom: 10 }} justify="space-between" align="middle">
                <Col>
                    <b style={{ fontSize: 20, color: '#071C50' }}>
                        Thống kê nhân sự Đảng/Đoàn viên theo phòng ban
                    </b>
                </Col>
                <Col>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <DatePicker
                            value={selectedDate}
                            onChange={handleDatePickerChange}
                            format="DD/MM/YYYY"
                            placeholder="Chọn ngày"
                            allowClear={false}
                            style={{ width: 130 }}
                        />
                    </div>
                </Col>
            </Row>

            <div className="card" style={{ padding: '16px 20px' }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Spin tip="Đang tải dữ liệu..." />
                    </div>
                ) : data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 0', color: '#888' }}>
                        Không có dữ liệu
                    </div>
                ) : (
                    <>
                        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Tổng nhân sự</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#071C50' }}>{tongSoNhanVien}</div>
                                </div>
                            </Col>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Số phòng ban</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#185FA5' }}>{data.length}</div>
                                </div>
                            </Col>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Tổng Đảng viên</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#D4537E' }}>{tongDangVien}</div>
                                </div>
                            </Col>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Đảng viên chính thức</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#D4537E' }}>{tongDangVienCT}</div>
                                </div>
                            </Col>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Tổng Đoàn viên</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#185FA5' }}>{tongDoanVien}</div>
                                </div>
                            </Col>
                            <Col>
                                <div style={cardStyle}>
                                    <div style={{ fontSize: 12, color: '#888780', marginBottom: 2 }}>Đoàn viên trưởng thành</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#185FA5' }}>{tongDoanVienTT}</div>
                                </div>
                            </Col>
                        </Row>
                        
                        <div style={{ position: 'relative', height: 280, marginBottom: 8 }}>
                            <canvas ref={chartRef} role="img" aria-label="Biểu đồ cột thống kê Đảng - Đoàn theo phòng ban" />
                        </div>
                        
                        <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
                            {[
                                { color: '#D4537E', label: 'Đảng viên chính thức' },
                                { color: '#BA7517', label: 'Đảng viên dự bị' },
                                { color: '#185FA5', label: 'Đoàn viên trưởng thành' },
                                { color: '#533AB7', label: 'Đoàn viên' },
                            ].map(item => (
                                <span key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#5F5E5A' }}>
                                    <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color, display: 'inline-block' }} />
                                    {item.label}
                                </span>
                            ))}
                        </div>
                        
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: '#eef2f9' }}>
                                        <th style={thStyle} rowSpan={2}>STT</th>
                                        <th style={{ ...thStyle, textAlign: 'left' }} rowSpan={2}>Phòng ban</th>
                                        <th style={thStyle} rowSpan={2}>Tổng nhân sự</th>
                                        <th style={thStyle} colSpan={3}>Đảng viên</th>
                                        <th style={thStyle} colSpan={3}>Đoàn viên</th>
                                        <th style={thStyle} rowSpan={2}>Tỷ lệ Đảng / Đoàn</th>
                                    </tr>
                                    <tr style={{ background: '#e4ecf7' }}>
                                        <th style={thStyle}>Chính thức</th>
                                        <th style={thStyle}>Dự bị</th>
                                        <th style={thStyle}>Tổng</th>
                                        <th style={thStyle}>Trưởng thành</th>
                                        <th style={thStyle}>Đoàn viên</th>
                                        <th style={thStyle}>Tổng</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((item, i) => {
                                        const tongDang = item.soLuongDangVienChinhThuc + item.soLuongDangVienDuBi;
                                        const tongDV = item.soLuongDoanVienTruongThanh + item.soLuongDoanVienChuaTruongThanh;
                                        const pctDang = item.soLuongNhanVien > 0 ? (tongDang / item.soLuongNhanVien) * 100 : 0;
                                        const pctDV = item.soLuongNhanVien > 0 ? (tongDV / item.soLuongNhanVien) * 100 : 0;

                                        return (
                                            <tr key={item.idDonVi} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                <td style={tdStyle}>{i + 1}</td>
                                                <td style={{ ...tdStyle, textAlign: 'left' }}>
                                                    <b style={{ color: '#071C50' }}>{item.tenDonVi}</b>
                                                </td>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: '#071C50' }}>{item.soLuongNhanVien}</td>
                                                
                                                <td style={tdStyle}>{item.soLuongDangVienChinhThuc}</td>
                                                <td style={tdStyle}>{item.soLuongDangVienDuBi}</td>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: '#D4537E' }}>{tongDang}</td>
                                                
                                                <td style={tdStyle}>{item.soLuongDoanVienTruongThanh}</td>
                                                <td style={tdStyle}>{item.soLuongDoanVienChuaTruongThanh}</td>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: '#185FA5' }}>{tongDV}</td>
                                                
                                                <td style={tdStyle}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <div style={{ width: 50, height: 5, background: '#e0e6f0', borderRadius: 3, overflow: 'hidden' }}>
                                                                <div style={{ width: `${pctDang}%`, height: '100%', background: '#D4537E', borderRadius: 3 }} />
                                                            </div>
                                                            <span style={{ fontSize: 11, color: '#D4537E', minWidth: 36, textAlign: 'left' }}>
                                                                {pctDang.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <div style={{ width: 50, height: 5, background: '#e0e6f0', borderRadius: 3, overflow: 'hidden' }}>
                                                                <div style={{ width: `${pctDV}%`, height: '100%', background: '#185FA5', borderRadius: 3 }} />
                                                            </div>
                                                            <span style={{ fontSize: 11, color: '#185FA5', minWidth: 36, textAlign: 'left' }}>
                                                                {pctDV.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr style={{ background: '#f0f4fa', fontWeight: 600 }}>
                                        <td style={tdStyle} colSpan={2}>Tổng cộng</td>
                                        <td style={{ ...tdStyle, color: '#071C50' }}>{tongSoNhanVien}</td>

                                        <td style={tdStyle}>{tongDangVienCT}</td>
                                        <td style={tdStyle}>{tongDangVienDuBi}</td>
                                        <td style={{ ...tdStyle, color: '#D4537E' }}>{tongDangVien}</td>

                                        <td style={tdStyle}>{tongDoanVienTT}</td>
                                        <td style={tdStyle}>{tongDoanVienChuaTT}</td>
                                        <td style={{ ...tdStyle, color: '#185FA5' }}>{tongDoanVien}</td>

                                        <td style={tdStyle}>
                                            <span style={{ color: '#D4537E' }}>
                                                {tongSoNhanVien > 0 ? ((tongDangVien / tongSoNhanVien) * 100).toFixed(1) : 0}%
                                            </span>
                                            {' / '}
                                            <span style={{ color: '#185FA5' }}>
                                                {tongSoNhanVien > 0 ? ((tongDoanVien / tongSoNhanVien) * 100).toFixed(1) : 0}%
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </Col>
    );
};

const thStyle: React.CSSProperties = {
    padding: '8px 12px', textAlign: 'center',
    fontWeight: 600, color: '#071C50', fontSize: 12, whiteSpace: 'nowrap',
    border: '1px solid #d9d9d9'
};

const tdStyle: React.CSSProperties = {
    padding: '7px 12px', textAlign: 'center', color: '#3d3d3a',
    border: '1px solid #f0f0f0'
};

export default ThongKeNhanSuDangDoanPhongBan;